import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Input,
  Button,
  Field,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableBody,
  TableCell,
  TableCellLayout,
  Dropdown,
  Option,
  Spinner,
  Checkbox,
} from "@fluentui/react-components";
import { Add20Regular, Delete20Regular, Add20Filled, Edit20Filled, Dismiss20Regular, Save20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import SearchResourceInGraph from "../GenericComponent/SearchResourceInGraph";
import { getAccessToken } from "../../Services/GraphServices";
import { GetGroup, AddGroup, UpdateGroup, getAllGroups } from "../../Services/HeedifyServices";
import "../CSS/GroupForm.css";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';

export default function GroupForm({ ACTIVE_TENANT, SCOPES, setGroups }) {
  const { t } = useTranslation();
  const { groupId, scopeParam } = useParams(); // from route
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const navigate = useNavigate();
  let userId = null;
  if (activeAccount) {
    userId = activeAccount.localAccountId;
  }

  // Determine mode based on groupId
  const isCreateMode = groupId === "newGroup";
  const isEditMode = !isCreateMode;

  const [groupData, setGroupData] = useState({
    PartitionKey: ACTIVE_TENANT,
    groupName: "",
    scope: "",
    agentsIds: [],
    agentDetails: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [errors, setErrors] = useState({});
  const [formValid, setFormValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialScope, setInitialScope] = useState(null); // Pour suivre le scope initial en mode édition
  const [agentsByScope, setAgentsByScope] = useState({}); // Sauvegarder les agents par scope
  
  // 🔐 Ability pour permissions
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();
  

  // ⚡ Calculer et ajouter permissions dans groupData à chaque changement de scope
  useEffect(() => {
    if (!ability || !groupData.scope) return;

    const scope = groupData.scope;
    const calculatedPermissions = {

      agentList: {
        canCreate: ability.can("create", `Group.Agents@${scope}`),
        canRead: ability.can("read", `Group.Agents@${scope}`),
        canUpdate: ability.can("update", `Group.Agents@${scope}`),
        canDelete: ability.can("delete", `Group.Agents@${scope}`),
      }
    };
    console.log("Calculated permissions for scope", scope, calculatedPermissions);
    setGroupData(prev => ({ ...prev, permissions: calculatedPermissions }));
  }, [ability, groupData.scope]);

  // 🔄 Réinitialiser la liste des agents lors du changement de scope
  useEffect(() => {
    if (!groupData.scope) return;
    
    const currentScope = groupData.scope;
    
    // Restaurer les agents du scope sélectionné (s'ils existent)
    if (agentsByScope[currentScope]) {
      setGroupData(prev => ({
        ...prev,
        agentsIds: agentsByScope[currentScope].agentsIds || [],
        agentDetails: agentsByScope[currentScope].agentDetails || {}
      }));
    } else {
      // Nouveau scope : vider les agents
      setGroupData(prev => ({
        ...prev,
        agentsIds: [],
        agentDetails: {}
      }));
    }
  }, [groupData.scope]);
  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!groupData.groupName)
      newErrors.groupName = t("THIS_FIELD_IS_REQUIRED");
    if (!groupData.scope)
      newErrors.scope = t("THIS_FIELD_IS_REQUIRED");

    setErrors(newErrors);

    // Retourne true si aucun champ vide
    const isValid = Object.keys(newErrors).length === 0;
    setFormValid(isValid);
    return isValid;
  };

  // Validate on field changes
  useEffect(() => {
    if (!isLoading) {
      validateForm();
    }
  }, [groupData.groupName, groupData.scope, isLoading]);

  // Function to refresh groups list
  const refreshGroupsList = (token) => {
    const scopeNames = Object.keys(SCOPES || {});
    const groupsPromises = scopeNames.map((s) =>
      getAllGroups(ACTIVE_TENANT, s, userId, token)
    );
    Promise.all(groupsPromises).then((data) => setGroups(data.flat()));
  };
  // Load existing group for edit
  useEffect(() => {
    const loadGroup = async () => {
      // In create mode, just stop loading
      if (isCreateMode) {
        setIsLoading(false);
        return;
      }

      // In edit mode, fetch the group data
      setIsLoading(true);
      try {
        const token = await getAccessToken(instance, activeAccount);
        const group = await GetGroup(
          ACTIVE_TENANT,
          scopeParam,
          groupId,
          userId,
          token
        );
        if (group) {
          const loadedScope = group.scope || "";
          setGroupData({
            PartitionKey: group.PartitionKey || ACTIVE_TENANT,
            id: group.id || "",
            groupName: group.groupName || "",
            scope: loadedScope,
            agentsIds: group.agentsIds || [],
            agentDetails: group.agentDetails || {},
          });
          // Sauvegarder le scope initial et ses agents
          setInitialScope(loadedScope);
          setAgentsByScope({
            [loadedScope]: {
              agentsIds: group.agentsIds || [],
              agentDetails: group.agentDetails || {}
            }
          });
        }
      } catch (err) {
        console.error("Error loading group:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadGroup();
  }, [groupId, ACTIVE_TENANT, instance, activeAccount, scopeParam, isCreateMode]);

  const updateField = (field, value) => {
    // Si on change le scope, sauvegarder les agents du scope actuel avant le changement
    if (field === 'scope' && groupData.scope && groupData.scope !== value) {
      setAgentsByScope(prev => ({
        ...prev,
        [groupData.scope]: {
          agentsIds: groupData.agentsIds,
          agentDetails: groupData.agentDetails
        }
      }));
    }
    setGroupData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAgent = (user) => {
    if (!user?.id) return;
    setGroupData((prev) => {
      if (prev.agentsIds.includes(user.id)) return prev;
      return {
        ...prev,
        agentsIds: [...prev.agentsIds, user.id],
        agentDetails: {
          ...prev.agentDetails,
          [user.id]: {
            displayName: user.displayName || user.header,
            email: user.email,
            isSupervisor: false,
          },
        },
      };
    });
  };

  const toggleSupervisor = (agentId) => {
    setGroupData((prev) => ({
      ...prev,
      agentDetails: {
        ...prev.agentDetails,
        [agentId]: {
          ...prev.agentDetails[agentId],
          isSupervisor: !prev.agentDetails[agentId]?.isSupervisor,
        },
      },
    }));
  };

  const handleRemoveAgent = (id) => {
    setGroupData((prev) => {
      const updatedIds = prev.agentsIds.filter((a) => a !== id);
      const updatedDetails = { ...prev.agentDetails };
      delete updatedDetails[id];
      return { ...prev, agentsIds: updatedIds, agentDetails: updatedDetails };
    });
  };
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    setSaving(true);
    
    return getAccessToken(instance, activeAccount)
      .then((token) => {

        // Supprimer permissions avant envoi
        const { permissions: _, ...groupDataToSave } = groupData;
        if (isEditMode) {
          // Update existing group
          return UpdateGroup(
            ACTIVE_TENANT,
            groupData.scope,
            userId,
            groupData.id,
            groupDataToSave,
            token
          ).then((response) => {
            if (response?.success === true && response?.message === 'Updated') {
              refreshGroupsList(token);
              showSuccess(t("GROUP_UPDATED_SUCCESS"));
              navigate("/groups");
            } else {
              const errorMsg = response?.message || "Erreur inconnue";
              console.error("Échec mise à jour groupe:", response);
              showError(t("GROUP_UPDATE_FAILED"), errorMsg);
            }
          });
        } else {
          // Create new group
          return AddGroup(
            ACTIVE_TENANT,
            groupData.scope,
            userId,
            groupDataToSave,
            token
          ).then((response) => {
            if (response?.success === true && response?.message === 'Created') {
              refreshGroupsList(token);
              showSuccess(t("GROUP_CREATED_SUCCESS"));
              navigate("/groups");
            } else {
              const errorMsg = response?.message || "Erreur inconnue";
              console.error("Échec création groupe:", response);
              showError(t("GROUP_CREATION_FAILED"), errorMsg);
            }
          });
        }
      })
      .catch((err) => {
        console.error("Error saving group:", err);
        showError(isEditMode ? t("GROUP_UPDATE_ERROR") : t("GROUP_CREATION_ERROR"), err?.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };


  return (
    <div className="group-form-root">

      <div className="group-form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button
          icon={<Dismiss20Regular />}
          appearance="secondary"
          onClick={() => navigate("/groups")}
          disabled={isLoading}
        >
          {t("CANCEL")}
        </Button>
        <Button
          appearance="primary"
          className="group-form-button-submit"
          icon={saving ? <Spinner size="tiny" /> : <Save20Regular />}
          onClick={handleSubmit}
          disabled={saving || isLoading || !formValid}
        >
          {isEditMode ? t("SAVE") : t("ADD")}
        </Button>
      </div>
      <div className="group-form-container" style={{ marginLeft: '8rem', marginRight: '8rem' }}>
        {/* Group Name */}
        <div className="group-form-group">
          <Field
            label={t("GROUP_NAME")}
            className="group-form-field"
            required
            validationState={errors.groupName ? "error" : "none"}
            validationMessage={errors.groupName}
          >
            <Input className="group-form-input" placeholder={t("ENTER_GROUP_NAME")} value={groupData.groupName} onChange={(e, data) => updateField("groupName", data.value)} />
          </Field>
        </div>

        {/* Scope Selector */}
        <div className="group-form-group">
          <Field
            label={t("SCOPE")}
            className="group-form-field"
            required
            validationState={errors.scope ? "error" : "none"}
            validationMessage={errors.scope}
          >
            <Dropdown
              placeholder={t("SELECT_SCOPE")}
              value={groupData.scope}
              selectedOptions={[groupData.scope]}
              onOptionSelect={(e, data) => updateField("scope", data.optionValue)}
              className="group-scope"
            >
              {Object.keys(SCOPES || {}).map((scope) => (
                <Option key={scope} value={scope}>
                  {scope}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        {/* Add Agents */}

        <div className="group-form-agents-section">
          <h3>{t("AGENT_LIST")}</h3>
         {(isEditMode ? groupData.permissions?.agentList?.canUpdate : groupData.permissions?.agentList?.canCreate) && (
            <Button className="group-form-button-add" appearance="primary" icon={<Add20Regular />} onClick={() => setShowUserSearch(true)} disabled={isLoading}>
            </Button>
          )}
          {showUserSearch && (
            <div className="group-form-search-container">
              <SearchResourceInGraph
                onSelectUser={(user) => {
                  handleAddAgent(user);
                  setShowUserSearch(false);
                }}
                onCancel={() => setShowUserSearch(false)}
                disabled={isLoading}
              />
            </div>
          )}

          {/* Agents Table */}
          {groupData.permissions?.agentList?.canRead && (<div className="group-form-table-container">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t("NAME")}</TableHeaderCell>
                  <TableHeaderCell>{t("EMAIL")}</TableHeaderCell>
                  <TableHeaderCell>{t("SUPERVISOR")}</TableHeaderCell>
                  <TableHeaderCell>{t("ACTION")}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupData.agentsIds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <TableCellLayout className="group-form-empty-state">{t("NO_AGENTS_ADDED")}</TableCellLayout>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupData.agentsIds.map((id) => {
                    const agent = groupData.agentDetails[id];
                    return (
                      <TableRow key={id}>
                        <TableCell><TableCellLayout>{agent?.displayName}</TableCellLayout></TableCell>
                        <TableCell><TableCellLayout>{agent?.email}</TableCellLayout></TableCell>
                        <TableCell>
                          <Checkbox
                            checked={agent?.isSupervisor || false}
                            onChange={() => toggleSupervisor(id)}
                            disabled={!groupData.permissions?.agentList?.canUpdate}
                          />
                        </TableCell>
                        <TableCell>
                          <Button icon={<Delete20Regular />} appearance="subtle" disabled={!groupData.permissions?.agentList?.canDelete} onClick={() => handleRemoveAgent(id)} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>)}
        </div>


        {/* Action Buttons */}
      </div>


    </div>
  );
}
